import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { BedDouble, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { totalCapacity } from '../../constants/roomConfig';

/**
 * Collapsible hotel/room-type/unit availability picker for
 * ParticipantBookingPanel's create-mode flow.
 */
const HotelAvailabilityList = ({ availability, selectedUnit, collapsedHotels, onToggleHotel, onSelectUnit, onOpenConfig }) => {
  const [hoveredUnit, setHoveredUnit] = useState(null); // { unitId, rect, occupied, capacity, rows } | null

  if (availability.length === 0) {
    return <p className="pbt-empty">No available rooms for this date range.</p>;
  }

  return (
    <div className="pbt-panel__hotels pbt-panel__hotels--scroll">
      {availability.map((hotel) => {
        const collapsed = collapsedHotels.has(hotel.hotel_id);
        return (
          <div key={hotel.hotel_id} className="pbt-hotel">
            <button
              type="button"
              className="pbt-hotel__toggle"
              onClick={() => onToggleHotel(hotel.hotel_id)}
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              <span className="pbt-hotel__name">{hotel.hotel_name}</span>
            </button>
            {!collapsed && hotel.room_types.map((roomType) => (
              <div key={roomType.room_id} className="pbt-hotel__room-type">
                <div className="pbt-hotel__room-type-label">
                  {roomType.room_type}
                  <Settings
                    size={13}
                    className="pbt-config-gear"
                    title={`Manage configurations for ${roomType.room_type}`}
                    onClick={() => onOpenConfig({ _id: roomType.room_id, room_name: roomType.room_type })}
                  />
                </div>
                <div className="pbt-hotel__units">
                  {roomType.units.map((unit) => {
                    const active = selectedUnit?.room_unit_id === unit.room_unit_id;
                    const configured = Boolean(unit.config_name);
                    const capacity = configured ? totalCapacity(unit.beds) : 0;
                    const metaParts = configured
                      ? [
                          ...(unit.beds || []).map((b) => `${b.count}× ${b.type}`),
                          ...(unit.features || []),
                        ]
                      : [];
                    const isFull = unit.beds_free <= 0;
                    const occupants = unit.occupants || [];
                    const occupantRows = occupants.map((o) => o.is_guest_only ? `${o.name} (Guest)` : o.name);
                    const occupantLabel = occupantRows.length > 0 ? occupantRows.join(', ') : null;
                    const Tag = isFull ? 'div' : 'button';

                    return (
                      <Tag
                        key={unit.room_unit_id}
                        {...(isFull ? {} : { type: 'button', onClick: () => onSelectUnit(hotel, roomType, unit) })}
                        className={`pbt-unit-btn${active ? ' pbt-unit-btn--active' : ''}${isFull ? ' pbt-unit-btn--full' : ''}`}
                        onMouseEnter={(e) => {
                          if (occupants.length > 0) {
                            const capacity = unit.beds_free + occupants.length;
                            setHoveredUnit({
                              unitId: unit.room_unit_id,
                              rect: e.currentTarget.getBoundingClientRect(),
                              occupied: occupants.length,
                              capacity,
                              rows: occupantRows,
                            });
                          }
                        }}
                        onMouseLeave={() => setHoveredUnit((h) => (h?.unitId === unit.room_unit_id ? null : h))}
                      >
                        <span className="pbt-unit-btn__top">
                          <span className="pbt-unit-btn__label-group">
                            <BedDouble size={13} />
                            <span className="pbt-unit-btn__label">{unit.room_label}</span>
                          </span>
                          <span className={`pbt-unit-btn__beds${isFull ? ' pbt-unit-btn__beds--full' : ''}`}>
                            {isFull ? 'Full' : `${unit.beds_free} free`}
                          </span>
                        </span>
                        <span className="pbt-unit-btn__price">
                          ${unit.price}<span className="pbt-unit-btn__price-unit">/night</span>
                        </span>
                        {configured && (
                          <span className="pbt-unit-btn__config-name">{unit.config_name}</span>
                        )}
                        {(capacity > 0 || metaParts.length > 0) && (
                          <span className="pbt-unit-btn__meta">
                            {[
                              ...(capacity > 0 ? [`Sleeps ${capacity}`] : []),
                              ...metaParts,
                            ].join(' · ')}
                          </span>
                        )}
                        {occupantLabel && (
                          <span className="pbt-unit-btn__occupants">
                            {isFull ? 'Occupied by: ' : 'Also: '}{occupantLabel}
                          </span>
                        )}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {hoveredUnit && createPortal(
        <div
          className="pbt-unit-occ-popover"
          style={{ top: hoveredUnit.rect.top, left: hoveredUnit.rect.right + 8 }}
        >
          <div className="pbt-unit-occ-popover__label">
            {hoveredUnit.occupied} of {hoveredUnit.capacity} occupied
          </div>
          <div className="pbt-unit-occ-popover__bar">
            <div
              className="pbt-unit-occ-popover__bar-fill"
              style={{ width: `${(hoveredUnit.occupied / hoveredUnit.capacity) * 100}%` }}
            />
          </div>
          {hoveredUnit.rows.map((r, i) => (
            <div key={r + i} className="pbt-unit-occ-popover__row">{r}</div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default HotelAvailabilityList;
